import {
  ExtendedParticipantField,
  ParticipantField,
  ParticipantFieldCategory,
  ParticipantFieldCategoryConfig,
  ParticipantFieldType,
  ProfileFieldCategory,
} from './types';
import { supportedLocales } from './valuesData/locales';
import { useTranslation } from 'react-i18next';
import {
  useTechSavviness,
  useCompanySizes,
  useCompanyTypes,
  useDevices,
  useEducationLevels,
  useFamilyStatuses,
  useGenders,
  useHierarchicalLevels,
  useIncomes,
  useIndustries,
  useProfessionalSituations,
} from './valuesData';

export const useCategories = (): ParticipantFieldCategoryConfig[] => {
  const { t } = useTranslation('common');
  return [
    {
      name: ParticipantFieldCategory.PERSONAL,
      icon: 'ri-user-3-line',
      iconColor: 'blue',
      title: t('participants.categories.personal'),
    },
    {
      name: ParticipantFieldCategory.DEMOGRAPHIC,
      icon: 'ri-pie-chart-line',
      iconColor: 'purple',
      title: t('participants.categories.demographic'),
      description: t('participants.categories.demographicDesc'),
    },
    {
      name: ParticipantFieldCategory.GEOLOCATION,
      icon: 'ri-map-pin-2-line',
      iconColor: 'green',
      title: t('participants.categories.geolocation'),
      titleClient: t('participants.categories.geolocation', {
        context: 'client',
      }),
      description: t('participants.categories.geolocationDesc'),
    },
    {
      name: ParticipantFieldCategory.DIGITAL_HABITS,
      icon: 'ri-smartphone-line',
      iconColor: 'yellow',
      title: t('participants.categories.digitalHabits'),
      titleClient: t('participants.categories.digitalHabits', {
        context: 'client',
      }),
      description: t('participants.categories.digitalHabitsDesc'),
    },
    {
      name: ParticipantFieldCategory.PROFESSIONAL,
      icon: 'ri-building-line',
      iconColor: 'red',
      title: t('participants.categories.professional'),
      description: t('participants.categories.professionalDesc'),
    },
    {
      name: ParticipantFieldCategory.MISC,
      icon: 'ri-error-warning-line',
      iconColor: 'ink',
      title: t('participants.categories.misc'),
    },
  ];
};

export const useParticipantFieldsData = (): ParticipantField[] => {
  const { t } = useTranslation('common');

  const companySizes = useCompanySizes();
  const companyTypes = useCompanyTypes();
  const devices = useDevices();
  const educationLevels = useEducationLevels();
  const familyStatuses = useFamilyStatuses();
  const genders = useGenders();
  const hierarchicalLevels = useHierarchicalLevels();
  const incomes = useIncomes();
  const industries = useIndustries();
  const professionalSituations = useProfessionalSituations();
  const techSavviness = useTechSavviness();

  return [
    {
      name: 'firstName',
      label: t('participants.labels.firstName'),
      placeholder: t('participants.placeholder.firstName'),
      required: true,
      type: ParticipantFieldType.SHORT_TEXT,
      category: ParticipantFieldCategory.PERSONAL,
      profileCategory: ProfileFieldCategory.CONTACT_DETAILS,
      csv: {
        columnAliases: ['prénom', 'firstname', 'first name', 'prenom'],
      },
      table: {
        Cell: 'RED_EXCLUDED',
      },
      customForm: {
        required: true,
      },
    },
    {
      name: 'lastName',
      label: t('participants.labels.lastName'),
      placeholder: t('participants.placeholder.lastName'),
      type: ParticipantFieldType.SHORT_TEXT,
      category: ParticipantFieldCategory.PERSONAL,
      profileCategory: ProfileFieldCategory.CONTACT_DETAILS,
      csv: {
        columnAliases: ['nom', 'lastname', 'last name', 'nom de famille'],
      },
      table: {
        Cell: 'RED_EXCLUDED',
      },
    },
    {
      name: 'email',
      label: t('participants.labels.email'),
      placeholder: t('participants.placeholder.email'),
      type: ParticipantFieldType.EMAIL,
      category: ParticipantFieldCategory.PERSONAL,
      profileCategory: ProfileFieldCategory.CONTACT_DETAILS,
      required: true,
      csv: {
        columnAliases: ['mail', 'email', 'e-mail'],
      },
      table: {
        Cell: 'RED_EXCLUDED',
      },
      customForm: {
        required: true,
      },
    },
    {
      name: 'phoneNumber',
      label: t('participants.labels.phoneNumber'),
      placeholder: t('participants.placeholder.phoneNumber'),
      type: ParticipantFieldType.PHONE_NUMBER,
      category: ParticipantFieldCategory.PERSONAL,
      profileCategory: ProfileFieldCategory.CONTACT_DETAILS,
      csv: {
        columnAliases: [
          'numéro',
          'num',
          'numéro de téléphone',
          'phone',
          'phonenumber',
          'téléphone',
          'telephone',
          'tel',
          'num de tel',
        ],
      },
      table: {
        singleLine: true,
      },
    },
    {
      name: 'countryKey',
      label: t('participants.labels.country'),
      type: ParticipantFieldType.SINGLE_CHOICE,
      category: ParticipantFieldCategory.GEOLOCATION,
      profileCategory: ProfileFieldCategory.PERSONAL,
      choices: [], // Added later with data from the API
      csv: {
        columnAliases: [
          'pays',
          'country',
          'countrykey',
          'country key',
          'clé pays',
        ],
      },
      table: {
        useArrayFilter: true,
      },
      criterion: {
        locked: true,
      },
    },
    {
      name: 'city',
      label: t('participants.labels.city'),
      placeholder: t('participants.placeholder.city'),
      type: ParticipantFieldType.GEOLOCATION,
      category: ParticipantFieldCategory.GEOLOCATION,
      profileCategory: ProfileFieldCategory.PERSONAL,
      csv: {
        columnAliases: ['ville', 'city'],
      },
      criterion: {
        description: t('criterion.description.city'),
      },
    },
    {
      name: 'postalCode',
      label: t('participants.labels.postalCode'),
      placeholder: t('participants.placeholder.postalCode'),
      type: ParticipantFieldType.LONG_TEXT,
      category: ParticipantFieldCategory.GEOLOCATION,
      profileCategory: ProfileFieldCategory.PERSONAL,
      csv: {
        columnAliases: ['code postal', 'postalcode', 'postal code'],
      },
      table: {
        filter: 'startsWith',
      },
    },
    {
      name: 'gender',
      label: t('participants.labels.gender'),
      placeholder: t('participants.placeholder.gender'),
      type: ParticipantFieldType.SINGLE_CHOICE,
      category: ParticipantFieldCategory.DEMOGRAPHIC,
      profileCategory: ProfileFieldCategory.PERSONAL,
      choices: genders,
      csv: {
        columnAliases: ['genre', 'gender', 'sexe'],
      },
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'jobTitle',
      label: t('participants.labels.jobTitle'),
      placeholder: t('participants.placeholder.jobTitle'),
      type: ParticipantFieldType.SHORT_TEXT,
      category: ParticipantFieldCategory.PROFESSIONAL,
      profileCategory: ProfileFieldCategory.PROFESSIONAL,
      csv: {
        columnAliases: ['jobtitle', 'job title', 'job', 'métier', 'poste'],
      },
      criterion: {
        description: t('criterion.description.jobTitle'),
      },
    },
    {
      name: 'birthdate',
      label: t('participants.labels.birthdate'),
      type: ParticipantFieldType.DATE,
      category: ParticipantFieldCategory.DEMOGRAPHIC,
      profileCategory: ProfileFieldCategory.PERSONAL,
      displayAsAge: true,
      range: {
        step: 5,
        label: t('participants.labels.age'),
        min: 18,
        max: 65,
      },
      validation: {
        min: 18,
      },
      csv: {
        guide: t('participants.dateGuide'),
        columnAliases: ['birth date', 'date de naissance', 'birth date'],
      },
    },
    {
      name: 'tags',
      label: t('participants.labels.tags'),
      placeholder: t('participants.placeholder.tags'),
      type: ParticipantFieldType.TAGS,
      category: ParticipantFieldCategory.MISC,
      profileCategory: ProfileFieldCategory.OTHERS,
      csv: {
        columnAliases: ['tags', 'tag'],
      },
      table: {
        backupKey: 'tagIds',
      },
      customForm: {
        available: false,
      },
    },
    {
      name: 'devices',
      label: t('participants.labels.devices'),
      type: ParticipantFieldType.MULTIPLE_CHOICE,
      category: ParticipantFieldCategory.DIGITAL_HABITS,
      profileCategory: ProfileFieldCategory.OWNED_DEVICES,
      choices: devices,
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'educationLevel',
      label: t('participants.labels.educationLevel'),
      type: ParticipantFieldType.SINGLE_CHOICE,
      category: ParticipantFieldCategory.DEMOGRAPHIC,
      profileCategory: ProfileFieldCategory.PERSONAL,
      csv: {
        columnAliases: ['Education', 'diploma', 'Diplome'],
      },
      choices: educationLevels,
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'professionalSituation',
      type: ParticipantFieldType.SINGLE_CHOICE,
      category: ParticipantFieldCategory.DEMOGRAPHIC,
      profileCategory: ProfileFieldCategory.PERSONAL,
      label: t('participants.labels.professionalSituation'),
      csv: {
        columnAliases: ['Situation professionnelle', 'Professional situation'],
      },
      choices: professionalSituations,
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'familyStatus',
      label: t('participants.labels.familyStatus'),
      type: ParticipantFieldType.SINGLE_CHOICE,
      category: ParticipantFieldCategory.DEMOGRAPHIC,
      profileCategory: ProfileFieldCategory.PERSONAL,
      csv: {
        columnAliases: ['family status', 'Situation familiale'],
      },
      choices: familyStatuses,
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'techSavviness',
      label: t('participants.labels.techSavviness'),
      type: ParticipantFieldType.SINGLE_CHOICE,
      category: ParticipantFieldCategory.DIGITAL_HABITS,
      profileCategory: ProfileFieldCategory.OWNED_DEVICES,
      csv: {
        columnAliases: ['tech', 'savviness', 'tect savvy', 'aisance'],
      },
      choices: techSavviness,
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'hierarchicalLevel',
      label: t('participants.labels.hierarchicalLevel'),
      type: ParticipantFieldType.SINGLE_CHOICE,
      category: ParticipantFieldCategory.PROFESSIONAL,
      profileCategory: ProfileFieldCategory.PROFESSIONAL,
      csv: {
        columnAliases: ['Niveau hierarchique', 'hierarchical level'],
      },
      choices: hierarchicalLevels,
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'industries',
      label: t('participants.labels.industries'),
      type: ParticipantFieldType.MULTIPLE_CHOICE,
      category: ParticipantFieldCategory.PROFESSIONAL,
      profileCategory: ProfileFieldCategory.PROFESSIONAL,
      csv: {
        columnAliases: ['Industrie'],
      },
      choices: industries,
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'companySize',
      label: t('participants.labels.companySize'),
      type: ParticipantFieldType.SINGLE_CHOICE,
      category: ParticipantFieldCategory.PROFESSIONAL,
      profileCategory: ProfileFieldCategory.PROFESSIONAL,
      csv: {
        columnAliases: [
          'Company Size',
          'size',
          'taille',
          "taille d'entreprise",
        ],
      },
      choices: companySizes,
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'companyType',
      label: t('participants.labels.companyType'),
      type: ParticipantFieldType.SINGLE_CHOICE,
      category: ParticipantFieldCategory.PROFESSIONAL,
      profileCategory: ProfileFieldCategory.PROFESSIONAL,
      csv: {
        columnAliases: ['Company type', "type d'entreprise"],
      },
      choices: companyTypes,
      table: {
        useArrayFilter: true,
      },
    },
    {
      name: 'income',
      label: t('participants.labels.income'),
      type: ParticipantFieldType.INCOME,
      category: ParticipantFieldCategory.DEMOGRAPHIC,
      profileCategory: ProfileFieldCategory.PERSONAL,
      csv: {
        columnAliases: ['Income', 'Revenue', 'revenues', 'Salaire', 'Salary'],
      },
      choices: incomes,
      table: {
        useArrayFilter: true,
      },
      criterion: {
        disabled: true,
      },
    },
    {
      name: 'locales',
      label: t('participants.labels.locales'),
      type: ParticipantFieldType.LOCALES,
      category: ParticipantFieldCategory.GEOLOCATION,
      profileCategory: ProfileFieldCategory.PERSONAL,
      labelVariants: {
        locales_restricted: t('participants.labels.locales_restricted'),
      },
      criterion: {
        aliases: ['locales', 'locales_restricted'],
      },
      choices: supportedLocales.map((locale) => ({
        label: t('locales.' + locale),
        value: locale,
      })),
      customForm: {
        available: false,
      },
    },
    {
      name: 'timezone',
      label: t('participants.labels.timezone'),
      type: ParticipantFieldType.TIMEZONE,
      category: ParticipantFieldCategory.MISC,
      profileCategory: ProfileFieldCategory.OTHERS,
      csv: {
        columnAliases: ['Fuseau'],
      },
      customForm: {
        available: false,
      },
    },
  ];
};

export const useExtendedParticipantFieldsData =
  (): ExtendedParticipantField[] => {
    const { t } = useTranslation('common');
    const participantFields = useParticipantFieldsData();
    return [
      ...participantFields,
      {
        name: 'lastParticipationDate',
        label: t('participants.labels.lastParticipationDate'),
        type: ParticipantFieldType.DATE,
        category: ParticipantFieldCategory.MISC,
        readOnly: true,
      },

      {
        name: 'excluded',
        label: t('participants.labels.excluded'),
        type: ParticipantFieldType.BOOLEAN,
        category: ParticipantFieldCategory.MISC,
        readOnly: true,
      },
      {
        name: 'evaluationScore',
        label: t('participants.labels.evaluationScore'),
        type: ParticipantFieldType.NUMBER,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
      {
        name: 'signupChecksScore',
        label: t('participants.labels.signupChecksScore'),
        type: ParticipantFieldType.NUMBER,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },

      {
        name: 'invitationCount',
        label: t('participants.labels.invitationCount'),
        type: ParticipantFieldType.NUMBER,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
      {
        name: 'clickOnInvitationCount',
        label: t('participants.labels.clickOnInvitationCount'),
        type: ParticipantFieldType.NUMBER,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
      {
        name: 'applicationCount',
        label: t('participants.labels.applicationCount'),
        type: ParticipantFieldType.NUMBER,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
      {
        name: 'participationCount',
        label: t('participants.labels.participationCount'),
        type: ParticipantFieldType.NUMBER,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
      {
        name: 'activityScore',
        label: t('participants.labels.activityScore'),
        type: ParticipantFieldType.NUMBER,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
      {
        name: 'score',
        label: t('participants.labels.score'),
        type: ParticipantFieldType.NUMBER,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
      {
        name: 'lastInvitation',
        label: t('participants.labels.lastInvitation'),
        type: ParticipantFieldType.DATE,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
      {
        name: 'lastParticipation',
        label: t('participants.labels.lastParticipation'),
        type: ParticipantFieldType.DATE,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
      {
        name: 'hasABookedAppointment',
        label: t('participants.labels.hasABookedAppointment'),
        type: ParticipantFieldType.BOOLEAN,
        category: ParticipantFieldCategory.STATS,
        readOnly: true,
      },
    ];
  };
